import React, { useState, useEffect } from 'react';
import { Mail, Clock, CheckCircle, XCircle, Loader2, Eye, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (contactId, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}?status=${status}`);
      fetchContacts();
      if (selectedContact?.id === contactId) {
        setSelectedContact({ ...selectedContact, status });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    if (filter === 'all') return true;
    return contact.status === filter;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <Badge className="bg-blue-100 text-blue-800">New</Badge>;
      case 'read':
        return <Badge className="bg-yellow-100 text-yellow-800">Read</Badge>;
      case 'replied':
        return <Badge className="bg-green-100 text-green-800">Replied</Badge>;
      case 'archived':
        return <Badge className="bg-gray-100 text-gray-800">Archived</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex gap-6 h-[calc(100vh-8rem)]">
        {/* Contacts List */}
        <div className="w-1/2 flex flex-col">
          <div className="mb-4">
            <h1 className="text-3xl font-bold">Contacts</h1>
            <p className="text-gray-600">{contacts.length} total messages</p>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 mb-4">
            {['all', 'new', 'read', 'replied', 'archived'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
                {status === 'new' && contacts.filter(c => c.status === 'new').length > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {contacts.filter(c => c.status === 'new').length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Contacts List */}
          <div className="flex-1 overflow-y-auto space-y-2">
            {filteredContacts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No contacts found
              </div>
            ) : (
              filteredContacts.map(contact => (
                <Card
                  key={contact.id}
                  className={`border-2 cursor-pointer transition-all ${
                    selectedContact?.id === contact.id
                      ? 'border-teal-500 bg-teal-50'
                      : 'hover:border-gray-300'
                  } ${contact.status === 'new' ? 'border-l-4 border-l-blue-500' : ''}`}
                  onClick={() => {
                    setSelectedContact(contact);
                    if (contact.status === 'new') {
                      updateStatus(contact.id, 'read');
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{contact.name}</span>
                        {getStatusBadge(contact.status)}
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDate(contact.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-1">{contact.email}</p>
                    <p className="text-sm text-gray-700 line-clamp-2">{contact.message}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Contact Detail */}
        <div className="w-1/2">
          {selectedContact ? (
            <Card className="border-2 h-full flex flex-col">
              <CardContent className="p-6 flex-1 flex flex-col">
                {/* Header */}
                <div className="border-b pb-4 mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-2xl font-bold">{selectedContact.name}</h2>
                      <a 
                        href={`mailto:${selectedContact.email}`}
                        className="text-teal-600 hover:underline flex items-center gap-1"
                      >
                        <Mail className="h-4 w-4" />
                        {selectedContact.email}
                      </a>
                    </div>
                    {getStatusBadge(selectedContact.status)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                    <Clock className="h-4 w-4" />
                    {formatDate(selectedContact.createdAt)}
                  </div>
                </div>

                {/* Message */}
                <div className="flex-1 overflow-y-auto">
                  <h3 className="font-semibold mb-2">Message:</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedContact.message}</p>
                </div>

                {/* Actions */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium mb-2">Update Status:</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateStatus(selectedContact.id, 'new')}
                      disabled={selectedContact.status === 'new'}
                    >
                      Mark as New
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateStatus(selectedContact.id, 'read')}
                      disabled={selectedContact.status === 'read'}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Mark as Read
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600"
                      onClick={() => updateStatus(selectedContact.id, 'replied')}
                      disabled={selectedContact.status === 'replied'}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Mark as Replied
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-gray-600"
                      onClick={() => updateStatus(selectedContact.id, 'archived')}
                      disabled={selectedContact.status === 'archived'}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Archive
                    </Button>
                  </div>

                  {/* Reply Button */}
                  <div className="mt-4">
                    <a href={`mailto:${selectedContact.email}?subject=Re: Contact from DadConnect`}>
                      <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                        <Mail className="h-4 w-4 mr-2" />
                        Reply via Email
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Mail className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>Select a contact to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContacts;
